import { App } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { pairApiWrapper } from '../../../entities/pair';
import { configService } from '../../../services/configService';
import { updateLastAction } from '../../../services/userService';
import { wsService } from '../../../services/websocket';
import { Pair } from '../../../types/pair';
import { WSEntityCreatedMsg, WSEntitySkippedAsNoLicenseMsg, WSEntityType, WSMessageType } from '../../../types/websocket';
import { useSettingsStore } from '../../Settings/hooks/useSettingsStore';
import {
  HIGHLIGHT_DURATION_MS,
  MAX_RECORDS_DISPLAY,
  MAX_RECORDS_FETCH,
} from '../utils/constants';
import {
  calculateAveragePercent,
  calculateMigrationPercent,
  hasRelatedPairMatchingFilters,
  isFilterActuallyEnabled,
  isValueInRange,
} from '../utils/filterUtils';

type UsePairRecordsResult = {
  filteredRecords: Pair[];
  pairRecords: Pair[];
  loading: boolean;
  error: string | null;
  fetchPairRecords: () => Promise<void>;
  newlyAddedIds: Set<number>;
  isPaused: boolean;
  setIsPaused: (value: boolean) => void;
  clearPairRecords: () => void;
};

const normalizeAddresses = (input: string): Set<string> => {
  if (!input) return new Set();

  return new Set(
    input
      .split(/\r?\n/)
      .map((line) => line.trim().toLowerCase())
      .filter(Boolean)
  );
};

export const usePairRecords = (isHoveringPair: boolean = false): UsePairRecordsResult => {
  const { message } = App.useApp();
  const { general, filters, lists } = useSettingsStore();
  const [pairRecords, setPairRecords] = useState<Pair[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [licenseEnabled, setLicenseEnabled] = useState<boolean>(true); // Default to true to avoid flicker

  // Fetch license enabled setting from API
  useEffect(() => {
    configService.getPublicConfig().then(config => {
      setLicenseEnabled(config.licenseEnabled);
    });
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [newlyAddedIds, setNewlyAddedIds] = useState<Set<number>>(new Set());
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const highlightTimeoutsRef = useRef<Map<number, number>>(new Map());
  const fetchInProgressRef = useRef<boolean>(false);
  const lastFetchTimeRef = useRef<number>(0);

  const clearHighlight = useCallback((id: number) => {
    setNewlyAddedIds(prev => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

    const timeoutId = highlightTimeoutsRef.current.get(id);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      highlightTimeoutsRef.current.delete(id);
    }
  }, []);

  const fetchPairRecords = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;

    // Prevent duplicate concurrent requests
    if (fetchInProgressRef.current) {
      return;
    }

    // Debounce: prevent requests within 500ms of each other (handles StrictMode double-mount)
    if (timeSinceLastFetch < 500) {
      return;
    }

    try {
      fetchInProgressRef.current = true;
      lastFetchTimeRef.current = now;
      setLoading(true);
      setError(null);

      // Update last action timestamp
      try {
        void updateLastAction();
      } catch (err) {
        // Silently fail - don't block the main data fetch
        console.warn('Failed to update last action:', err);
      }

      const response = await pairApiWrapper.getLastPairRecords(MAX_RECORDS_FETCH, true);
      if (response) {
        setPairRecords(response.data);
      } else {
        setError('Failed to fetch pair records. Please try again.');
      }
    } catch (err: any) {
      setError('Failed to fetch pair records. Please try again.');
      console.error('Error fetching pair records:', err);
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  }, []);

  const relatedMatchingMode = filters.relatedMatchingMode?.from === 'any' ? 'any' : 'latest';
  const devWhiteList = useMemo(() => normalizeAddresses(lists.devWhiteList), [lists.devWhiteList]);
  const devBlackList = useMemo(() => normalizeAddresses(lists.devBlackList), [lists.devBlackList]);

  // Parse selected protocols from filter
  const selectedProtocols = useMemo(() => {
    if (!filters.protocol?.from || filters.protocol.from.trim() === '') {
      return [];
    }
    try {
      const parsed = JSON.parse(filters.protocol.from);
      return Array.isArray(parsed) ? parsed : [];
    }
    catch {
      return [];
    }
  }, [filters.protocol?.from]);

  // Normalize selected protocols to lowercase for case-insensitive comparison
  const normalizedSelectedProtocols = useMemo(() => {
    return new Set(selectedProtocols.map(p => p?.toLowerCase().trim()).filter(Boolean));
  }, [selectedProtocols]);

  // Helper function to get the first filter name that failed for a record
  const getFirstFailingFilter = useCallback((record: Pair): string | null => {
    const motionPercentFilterEnabled = isFilterActuallyEnabled(filters.motionPercent);
    const bmotionPercentFilterEnabled = isFilterActuallyEnabled(filters.bmotionPercent);
    const maxMCPercentFilterEnabled = isFilterActuallyEnabled(filters.maxMCPercent);
    const openPriceFilterEnabled = isFilterActuallyEnabled(filters.openPrice);
    const migrationPercentFilterEnabled = isFilterActuallyEnabled(filters.migrationPercent);
    const devPairsCountFilterEnabled = isFilterActuallyEnabled(filters.devPairsCount);
    const motionGreaterBMotionEnabled = filters.motionGreaterBMotion.enabled;
    const motionAvgPercentFilterEnabled = isFilterActuallyEnabled(filters.motionAvgPercent);
    const bmotionAvgPercentFilterEnabled = isFilterActuallyEnabled(filters.bmotionAvgPercent);
    const maxMCAvgPercentFilterEnabled = isFilterActuallyEnabled(filters.maxMCAvgPercent);
    const twitterFollowersFilterEnabled = isFilterActuallyEnabled(filters.twitterFollowers);
    const twitterCommunityFilterEnabled = filters.twitterCommunity.enabled;
    const twitterVerifiedFilterEnabled = filters.twitterVerified.enabled;
    const twitterFollowingFilterEnabled = isFilterActuallyEnabled(filters.twitterFollowing);
    const twitterHasAccountFilterEnabled = filters.twitterHasAccount.enabled;
    const totalPairFeesFilterEnabled = isFilterActuallyEnabled(filters.totalPairFees);
    const hasRelatedPairFilters = motionPercentFilterEnabled || bmotionPercentFilterEnabled || maxMCPercentFilterEnabled ||
      openPriceFilterEnabled || totalPairFeesFilterEnabled || motionGreaterBMotionEnabled;

    // Check white/black list (addresses and twitter names)
    const normalizedDevAddress = record.devAddress?.trim().toLowerCase();
    const normalizedTwitterUsername = record.twitterUsername?.trim().toLowerCase();

    // Check if devAddress is in white list - passes filter
    if (normalizedDevAddress && devWhiteList.has(normalizedDevAddress)) {
      return null;
    }
    // Check if twitterUsername is in white list - passes filter
    if (normalizedTwitterUsername && devWhiteList.has(normalizedTwitterUsername)) {
      return null;
    }
    // Check if devAddress is in black list
    if (normalizedDevAddress && devBlackList.has(normalizedDevAddress)) {
      return 'Blacklist';
    }
    // Check if twitterUsername is in black list
    if (normalizedTwitterUsername && devBlackList.has(normalizedTwitterUsername)) {
      return 'Blacklist';
    }

    // Check protocol filter on the main pair (case-insensitive comparison)
    if (selectedProtocols.length === 0) return 'Protocol';
    if (!record.protocol) return 'Protocol';

    const normalizedRecordProtocol = record.protocol.toLowerCase().trim();
    if (!normalizedSelectedProtocols.has(normalizedRecordProtocol)) return 'Protocol';

    // If hideWithoutRelated is enabled, must have related pairs
    if (general.hideWithoutRelated) {
      if (!Array.isArray(record.relatedPairs) || record.relatedPairs.length === 0) {
        return 'No Related';
      }
    }

    // Check Dev Pairs Count filter on the main pair
    if (devPairsCountFilterEnabled) {
      if (!isValueInRange(record.devPairCount, filters.devPairsCount.from, filters.devPairsCount.to)) {
        return 'Dev Pairs';
      }
    }

    // Check Migration % filter on the main pair
    if (migrationPercentFilterEnabled) {
      const migrationPercent = calculateMigrationPercent(record.migrationCount, record.devPairCount);
      if (!isValueInRange(migrationPercent, filters.migrationPercent.from, filters.migrationPercent.to)) {
        return 'Migration %';
      }
    }

    // Check Twitter Has Account filter on the main pair
    if (twitterHasAccountFilterEnabled) {
      if (!record.twitter && !record.twitterUsername) {
        return 'Has Twitter Account';
      }
    }

    // Check Twitter Verified filter on the main pair
    if (twitterVerifiedFilterEnabled) {
      if (!record.twitterVerified) {
        return 'Twitter Verified';
      }
    }

    // Check Twitter Community filter on the main pair
    if (twitterCommunityFilterEnabled) {
      if (!record.twitterCommunity) {
        return 'Twitter Community';
      }
    }

    // Check Twitter Followers filter on the main pair
    if (twitterFollowersFilterEnabled) {
      if (!isValueInRange(record.twitterFollowers, filters.twitterFollowers.from, filters.twitterFollowers.to)) {
        return 'Twitter Admin Followers';
      }
    }

    // Check Twitter Following filter on the main pair
    if (twitterFollowingFilterEnabled) {
      if (!isValueInRange(record.twitterFollowing, filters.twitterFollowing.from, filters.twitterFollowing.to)) {
        return 'Twitter Admin Following';
      }
    }

    // Check average filters
    if (motionAvgPercentFilterEnabled) {
      const avgMotionPercent = calculateAveragePercent(record.relatedPairs, (pair) => pair.motionPercent);
      if (avgMotionPercent === null || !isValueInRange(avgMotionPercent, filters.motionAvgPercent.from, filters.motionAvgPercent.to || "")) {
        return 'Motion Avg %';
      }
    }

    if (bmotionAvgPercentFilterEnabled) {
      const avgBMotionPercent = calculateAveragePercent(record.relatedPairs, (pair) => pair.bmotionPercent);
      if (avgBMotionPercent === null || !isValueInRange(avgBMotionPercent, filters.bmotionAvgPercent.from, filters.bmotionAvgPercent.to || "")) {
        return 'BMotion Avg %';
      }
    }

    if (maxMCAvgPercentFilterEnabled) {
      const avgMaxMCPercent = calculateAveragePercent(record.relatedPairs, (pair) => pair.maxMCPercent);
      if (avgMaxMCPercent === null || !isValueInRange(avgMaxMCPercent, filters.maxMCAvgPercent.from, filters.maxMCAvgPercent.to || "")) {
        return 'MaxMC Avg %';
      }
    }

    // If any related pair filters are enabled, must have related pairs that match
    if (hasRelatedPairFilters) {
      if (!Array.isArray(record.relatedPairs) || record.relatedPairs.length === 0) {
        return 'No Related';
      }
      const howManyRelatedShouldMatch = parseInt(filters.howManyRelatedShouldMatch?.from || "1", 10) || 1;
      const matches = hasRelatedPairMatchingFilters(record.relatedPairs, {
        motionPercent: filters.motionPercent,
        bmotionPercent: filters.bmotionPercent,
        maxMCPercent: filters.maxMCPercent,
        openPrice: filters.openPrice,
        totalPairFees: filters.totalPairFees,
        migrationPercent: filters.migrationPercent,
        motionGreaterBMotion: filters.motionGreaterBMotion,
      }, howManyRelatedShouldMatch, relatedMatchingMode);
      if (!matches) return 'Related Filters';
    }

    // All filters passed
    return null;
  }, [
    general.hideWithoutRelated,
    filters.motionPercent,
    filters.bmotionPercent,
    filters.maxMCPercent,
    filters.openPrice,
    filters.totalPairFees,
    filters.migrationPercent,
    filters.devPairsCount,
    filters.motionGreaterBMotion,
    filters.howManyRelatedShouldMatch,
    filters.motionAvgPercent,
    filters.bmotionAvgPercent,
    filters.maxMCAvgPercent,
    filters.twitterCommunity,
    filters.twitterFollowers,
    filters.twitterVerified,
    filters.twitterFollowing,
    filters.twitterHasAccount,
    selectedProtocols,
    normalizedSelectedProtocols,
    relatedMatchingMode,
    devWhiteList,
    devBlackList,
  ]);

  // Helper function to check if a record matches current filters
  const doesRecordMatchFilters = useCallback((record: Pair): boolean => {
    return getFirstFailingFilter(record) === null;
  }, [getFirstFailingFilter]);

  const handleNewPairRecord = useCallback(
    (newRecord: Pair) => {
      if (isPaused || isHoveringPair) return;

      setPairRecords(prevRecords => {
        const exists = prevRecords.some(record => record.id === newRecord.id);
        if (exists) return prevRecords;

        const updatedRecords = [newRecord, ...prevRecords];
        return updatedRecords.slice(0, MAX_RECORDS_FETCH);
      });

      // Check if the record matches filters
      const failingFilter = getFirstFailingFilter(newRecord);

      if (failingFilter === null) {
        // Only add to newlyAddedIds and show success if it matches filters
        setNewlyAddedIds(prev => {
          const next = new Set(prev);
          next.add(newRecord.id);
          return next;
        });

        const timeoutId = window.setTimeout(() => {
          clearHighlight(newRecord.id);
        }, HIGHLIGHT_DURATION_MS);
        highlightTimeoutsRef.current.set(newRecord.id, timeoutId);

        message.success({
          content: `New record added: ${newRecord.tokenTicker}`,
          className: 'pair-message pair-message--success',
        });
      }
      else {
        // Show warning message for filtered pairs with the filter name
        message.warning({
          content: `New pair filtered out: ${newRecord.tokenTicker} (skip reason: ${failingFilter})`,
          className: 'pair-message pair-message--warning',
        });
      }
    },
    [clearHighlight, message, isPaused, isHoveringPair, getFirstFailingFilter]
  );

  useEffect(() => {
    fetchPairRecords();
  }, [fetchPairRecords]);

  useEffect(() => {
    const unsubscribeCreated = wsService.onMessage<WSEntityCreatedMsg<Pair>>(
      WSMessageType.entityCreated,
      wsMessage => {
        if (wsMessage.entityType === WSEntityType.pair) {
          handleNewPairRecord(wsMessage.entity);
        }
      }
    );

    const unsubscribeSkipped = wsService.onMessage<WSEntitySkippedAsNoLicenseMsg>(
      WSMessageType.entitySkippedAsNoLicense,
      wsMessage => {
        // Only show skipped messages if license feature is enabled
        if (licenseEnabled && wsMessage.entityType === WSEntityType.pair) {
          message.warning(`Pair "${wsMessage.pairName}" skipped as no active license`);
        }
      }
    );

    return () => {
      unsubscribeCreated();
      unsubscribeSkipped();
      highlightTimeoutsRef.current.forEach(timeoutId => window.clearTimeout(timeoutId));
      highlightTimeoutsRef.current.clear();
    };
  }, [handleNewPairRecord, message, licenseEnabled]);

  const clearPairRecords = useCallback(() => {
    setPairRecords([]);
    setNewlyAddedIds(new Set());
    highlightTimeoutsRef.current.forEach(timeoutId => window.clearTimeout(timeoutId));
    highlightTimeoutsRef.current.clear();
    message.info('All token cards cleared');
  }, [message]);


  const filteredRecords = useMemo(() => {
    const filtered = pairRecords.filter(doesRecordMatchFilters);
    // Apply MAX_RECORDS_DISPLAY limit to filtered results
    return filtered.slice(0, MAX_RECORDS_DISPLAY);
  }, [pairRecords, doesRecordMatchFilters]);

  return {
    filteredRecords,
    pairRecords,
    loading,
    error,
    fetchPairRecords,
    newlyAddedIds,
    isPaused,
    setIsPaused,
    clearPairRecords,
  };
};
